import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { Input } from 'components/Input/Input'
import { Modal } from 'components/Modal/Modal'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { HonestTimePickerInput } from 'components/HonestTimePicker/HonestTimePickerInput'
import { Button } from 'components/ui/Button/Button'
import { CursorPointer, Flex } from 'styling/GlobalStyles'
import { TitleText } from 'styling/Typography'
import useModalClose from 'hooks/useModalClose'
import useFocusFirstInput from 'utils/useFocusFirstInput'
import { IOpsDepartment } from 'types/opsDepartments.interface'
import { IOpsRaiseRequestForm } from 'types/opsToday.interface'
import { OpsRequestPriority } from 'types/opsRequests.interface'
import { raiseOpsRequest } from 'features/operations/opsRequestsSlice'
import { opsDepartments } from '../Shared/opsDepartments'
import { REQUEST_PRIORITY_META } from '../Shared/opsRequestsData'

const PRIORITY_KEYS: OpsRequestPriority[] = ['low', 'normal', 'high', 'critical']

const PRIORITY_OPTIONS = PRIORITY_KEYS.map((p) => ({
  label: REQUEST_PRIORITY_META[p].label,
  value: p,
}))

export const RaiseRequestModal = ({
  dept,
  closeModal,
}: {
  dept: IOpsDepartment
  closeModal: () => void
}) => {
  const [state, setState] = useState<IOpsRaiseRequestForm>({
    toDept: null,
    title: '',
    description: '',
    priority: 'normal',
    neededBy: '',
  })
  const dispatch = useDispatch<AppDispatch>()
  const profiles = useSelector((s: RootState) => s.deptOpsProfiles.profiles)
  useFocusFirstInput()
  useModalClose(closeModal)

  const deptOptions = opsDepartments(profiles)
    .filter((d) => d.key !== dept.key)
    .map((d) => ({ label: d.name, value: d.key }))

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, name: string) => {
    setState({ ...state, [name]: e.target.value })
  }

  const timeValid = Boolean(state.neededBy)
  const isValid =
    Boolean(state.toDept) && Boolean(state.title.trim()) && Boolean(state.description.trim()) && timeValid

  const handleSave = async () => {
    if (!state.toDept) return
    await dispatch(
      raiseOpsRequest({
        payload: {
          fromDeptKey: dept.key,
          toDeptKey: state.toDept,
          title: state.title.trim(),
          description: state.description.trim(),
          priority: state.priority,
          neededByTime: state.neededBy,
          profiles: opsDepartments(profiles),
        },
      }),
    )
    closeModal()
  }

  return (
    <Modal black>
      <Flex column between center>
        <Flex column gap='1rem' width='100%'>
          <Flex between width='100%' center style={{ marginBottom: '1rem' }}>
            <TitleText weight='bold'>Raise request</TitleText>
            <CursorPointer onClick={closeModal}>
              <CloseIcon />
            </CursorPointer>
          </Flex>
          <HonestSelectInput
            nomarg
            labelText='To department'
            name='toDept'
            placeholder='Who do you need?'
            options={deptOptions}
            required
            invalid={!state.toDept}
            onChange={(v: any) => setState((prev) => ({ ...prev, toDept: v?.value || null }))}
          />
          <Input
            nomarg
            labelText='Title'
            placeholder='What do you need — one line'
            value={state.title}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, 'title')}
            required
            invalid={!state.title.trim()}
          />
          <Input
            nomarg
            type='textarea'
            height='6rem'
            labelText='Description'
            placeholder='Give them enough detail to act without calling you back...'
            value={state.description}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(e, 'description')}
            required
            invalid={!state.description.trim()}
          />
          <HonestSelectInput
            nomarg
            labelText='Priority'
            name='priority'
            options={PRIORITY_OPTIONS}
            defaultValue='normal'
            onChange={(v: any) =>
              setState((prev) => ({ ...prev, priority: (v?.value || 'normal') as OpsRequestPriority }))
            }
          />
          <HonestTimePickerInput
            nomarg
            labelText='Needed by'
            onChange={(time) => setState((prev) => ({ ...prev, neededBy: time }))}
          />
        </Flex>
        <Button style={{ marginTop: '16px' }} disabled={!isValid} variant='primary' onClick={handleSave}>
          Save
        </Button>
      </Flex>
    </Modal>
  )
}
